import type { TFunction } from '@shared/types/types'
import {
  MAX_FILE_SIZE_IN_BYTES,
  MAX_FILE_SIZE_IN_MB,
  getZeroSizeValidationMsg,
  isFileSizeGreaterThanZero,
} from '@shared/uploads/ImageUploads.util'
import type { RefObject } from 'react'
import type { FieldValues, UseFormClearErrors, UseFormSetError } from 'react-hook-form'
import { ATTACHMENT_IMAGE_MIME_TYPES } from '../AttachmentsField'
import { getRichTextLength } from './getRichTextLength'
import type { RichTextEditor } from './types'
import type { AttachmentUploadFn } from './useFroalaAttachments'
import type useRichTextImageUpload from './useRichTextImageUpload'
import type { ImageUploadFn } from './useRichTextImageUpload'

type RichTextEditorEventsParams = {
  name: string
  t: TFunction
  charCounterMax: number
  imageUploadFn?: ImageUploadFn
  upload: ReturnType<typeof useRichTextImageUpload>['upload']
  setErrorRef: RefObject<UseFormSetError<FieldValues>>
  clearErrorsRef: RefObject<UseFormClearErrors<FieldValues>>
  onChangeRef: RefObject<((html: string) => void) | null>
  editorRef: RefObject<RichTextEditor | null>
  blurTimeoutRef: RefObject<ReturnType<typeof setTimeout> | null>
  setEditorInstanceForMentions: (editor: RichTextEditor) => void
  isExpanded: boolean
  setIsExpanded: (expanded: boolean) => void
  compactUntilFocus: boolean
  onExpandedChange?: (expanded: boolean) => void
  onEnterSubmit?: (currentValue: string) => void
  areGifsAvailable: boolean
  registerGifButtonCommand: () => void
  attachmentUploadFn?: AttachmentUploadFn
  attachmentHook: { registerAttachmentButtonCommand: () => void }
  disabled?: boolean
  autoFocus: boolean
}

/**
 * Builds the Froala `events` object for RichTextEditorField. Call it only inside the field hook's
 * editor-config memo: the handlers capture the values passed here, so every value that can change
 * between renders must also be a dependency of that memo.
 */
export const buildRichTextEditorEvents = ({
  name,
  t,
  charCounterMax,
  imageUploadFn,
  upload,
  setErrorRef,
  clearErrorsRef,
  onChangeRef,
  editorRef,
  blurTimeoutRef,
  setEditorInstanceForMentions,
  isExpanded,
  setIsExpanded,
  compactUntilFocus,
  onExpandedChange,
  onEnterSubmit,
  areGifsAvailable,
  registerGifButtonCommand,
  attachmentUploadFn,
  attachmentHook,
  disabled,
  autoFocus,
}: RichTextEditorEventsParams) => ({
  'paste.before': function () {
    // We only allow pasting if the field is expanded.
    return isExpanded
  },
  'paste.after': function () {
    const editor = this as unknown as RichTextEditor
    setTimeout(() => {
      const current = editor?.html?.get?.() ?? ''
      onChangeRef.current?.(current)
    }, 100)
  },
  contentChanged() {
    const editor = this as unknown as RichTextEditor
    const currentHtml = editor?.html?.get?.() ?? ''
    const length = getRichTextLength(currentHtml)
    if (length > charCounterMax) {
      setErrorRef.current?.(name, {
        type: 'maxLength',
        message: t('Maximum {max} characters allowed', {
          max: charCounterMax.toLocaleString(),
        }),
      })
    } else {
      clearErrorsRef.current?.(name)
    }
  },
  'image.beforeUpload': function (images: File[]) {
    const editor = this as unknown as RichTextEditor
    if (images.length > 0) {
      const img = images[0]
      // Clear any previous image upload errors
      clearErrorsRef.current?.(name)
      // If imageUploadFn is not provided, don't allow upload
      if (!imageUploadFn) {
        setErrorRef.current?.(name, {
          message: t('Image uploads are not enabled for this field'),
        })
        editor.popups.hideAll()
        return false
      }
      // Validate file type before upload
      const imageMimeTypes = ATTACHMENT_IMAGE_MIME_TYPES
      if (!imageMimeTypes.includes(img.type)) {
        setErrorRef.current?.(name, {
          message: t('Image must be JPG, PNG or WEBP'),
        })
        editor.popups.hideAll()
        return false
      }
      if (!isFileSizeGreaterThanZero(img)) {
        setErrorRef.current?.(name, {
          message: getZeroSizeValidationMsg(t),
        })
        editor.popups.hideAll()
        return false
      }
      // Validate file size before upload
      if (img.size > MAX_FILE_SIZE_IN_BYTES) {
        setErrorRef.current?.(name, {
          message: t(
            'The image exceeds the {maxFileSizeInMb}MB size limit, upload a smaller image',
            {
              maxFileSizeInMb: MAX_FILE_SIZE_IN_MB.toLocaleString(),
            },
          ),
        })
        editor.popups.hideAll()
        return false
      }
      upload(img, editor, name)
      return false
    }
    return true
  },
  ...(compactUntilFocus && {
    click: () => {
      if (!isExpanded) {
        setIsExpanded(true)
      } else if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current)
        blurTimeoutRef.current = null
      }
    },
  }),
  ...(compactUntilFocus &&
    isExpanded && {
      blur: () => {
        blurTimeoutRef.current = setTimeout(() => {
          const editorInstance = editorRef.current as unknown as {
            el?: HTMLElement
          }
          const activeEl = document.activeElement
          // Use the full editor box (.fr-box) so toolbar buttons/dropdowns (e.g. formatUL)
          // don't trigger collapse when focus moves from the editable area to the toolbar
          const editorBox = editorInstance?.el?.closest('.fr-box')
          const focusInEditor = editorBox?.contains(activeEl)
          // Keep expanded when focus moved to the mentions popup (Tribute.js)
          const focusInMentionsPopup = activeEl?.closest?.('.tribute-container')
          if (editorBox && !focusInEditor && !focusInMentionsPopup) {
            setIsExpanded(false)
          }
          blurTimeoutRef.current = null
        }, 150)
      },
    }),
  keydown(keydownEvent: KeyboardEvent & { originalEvent?: KeyboardEvent }) {
    const editor = this as unknown as RichTextEditor

    if (keydownEvent.key !== 'Enter') return
    if (!onEnterSubmit) return
    // Shift+Enter: allow default so the editor inserts a new line
    if (keydownEvent.shiftKey) return

    const nativeEvent = keydownEvent.originalEvent ?? keydownEvent
    nativeEvent.preventDefault()
    nativeEvent.stopPropagation()
    if (keydownEvent !== nativeEvent) {
      keydownEvent.preventDefault()
      keydownEvent.stopPropagation()
    }

    let currentValue = editor?.html?.get?.() ?? ''
    // Strip trailing empty block Froala may have inserted before we could prevent it
    currentValue = currentValue.replace(/\s*<p>(<br\s*\/?>)?\s*<\/p>\s*$/i, '')
    onEnterSubmit(currentValue)
    if (compactUntilFocus && isExpanded) setIsExpanded(false)
    return false
  },
  initialized() {
    const editor = this as unknown as RichTextEditor
    // Under StrictMode the Froala React wrapper can call this for the next editor before Froala has
    // loaded its modules; that editor's own initialized call follows once it is ready.
    if (!('events' in editor)) return
    editorRef.current = editor
    setEditorInstanceForMentions(editor)

    const initialHtml = editor?.html?.get?.() ?? ''
    if (getRichTextLength(initialHtml) > charCounterMax) {
      setErrorRef.current?.(name, {
        type: 'maxLength',
        message: t('Maximum {max} characters allowed', {
          max: charCounterMax.toLocaleString(),
        }),
      })
    }

    // Ensure buttons are registered when editor initializes
    if (areGifsAvailable) {
      registerGifButtonCommand()
    }
    if (attachmentUploadFn) {
      attachmentHook.registerAttachmentButtonCommand()
    }

    if (disabled) {
      editor.edit?.off()
    } else {
      editor.edit?.on()
    }

    // Handle autofocus after editor is fully initialized
    const shouldFocus = autoFocus || (compactUntilFocus && isExpanded)
    if (shouldFocus) {
      // Use setTimeout to ensure focus happens after editor is fully rendered
      setTimeout(() => {
        const editorInstance = editorRef.current as unknown as {
          el?: HTMLElement
        }
        // Focus the editor element directly (editor.el is the Froala editor DOM element)
        if (editorInstance?.el) {
          editorInstance.el.focus()
        }
      }, 0)
    }
    // Notify parent only when the expanded editor has finished initializing (avoids button flicker during expand)
    if (compactUntilFocus && isExpanded) {
      onExpandedChange?.(true)
    }
  },
})
